import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { Portal } from '@chakra-ui/react';
import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import {
  AddIcon,
  CloseIcon,
  DeleteIcon,
  ImageIcon,
  MoreIcon,
  TickMarkIcon,
} from '@sketchnote/icons';
import { Panel, Tabs } from '@sketchnote/shared-components';
import { Button } from '@sketchnote/shared-components';
import cn from 'classnames';
import { frontendHostUrl } from 'config';
import { Emoji, Picker } from 'emoji-mart';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { API, endpoints } from 'src/api';
import { CoverImage, getRandomCoverImage } from 'src/components/CoverImage';
import { useSidebarStore } from 'src/components/sidebar/store';
import { useFileStore } from 'src/features/files';
import { useFileService } from 'src/features/files';
import CreateEpic from 'src/features/scrum/components/epic/CreateEpic';
import CreateSprint from 'src/features/scrum/components/sprint/CreateSprint';
import { v4 as uuid } from 'uuid';

import { fileTagColor, viewsWithoutFileHeader } from '../assets/data';
import { useFileAccess } from '../hooks/useFileAccess';
import { FileTag, OrganisationTag } from '../types/file';

type recentImg = {
  src: string;
};

export const FileHeader = ({ fileId, type }: { fileId: string; type: string }): JSX.Element => {
  if (viewsWithoutFileHeader.includes(type)) return <div />;
  const [isOpenTagDropdown, setIsOpenTagDropdown] = useState(false);
  const {
    addCover,
    disableCover,
    getRecentIcons,
    updateRecentIcon,
    getRecentUploads,
    updateCoverImage,
  } = useFileService();
  const router = useRouter();
  const [inputTxt, setInputTxt] = useState('');
  const [tagtobeEdited, setTagToBeEdited] = useState<FileTag>();
  const [tagHoverId, setTagHoverId] = useState('');
  const [isOpenEditTagModal, setIsOpenEditTagModal] = useState(false);
  const [isOpenDeleteTagModal, setIsOpenDeleteTagModal] = useState(false);
  const templateId = router.query.templateId as string;
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [inputLabel, setInputLabel] = useState('');
  const [organisationTags, setOrganisationTags] = useState<OrganisationTag[]>();
  const imgUrlRef = React.useRef<any>(null);
  const hiddenFileInput = React.useRef<any>(null);
  const fetchFile = useFileStore((s) => s.fetchFile);
  const [recentIconUploads, setRecentIconUploads] = useState<recentImg[]>([]);

  const currentFile = useFileStore(useCallback((s) => s.files[fileId], [fileId]));

  const { isReadOnly } = useFileAccess();

  useEffect(() => {
    setOrganisationTags(currentFile?.organisationTags);
  }, [currentFile?.organisationTags]);

  const { onAddTag, onUpdateTag, onDeleteTag } = useFileStore((s) => ({
    onAddTag: s.onAddTag,
    onUpdateTag: s.onUpdateTag,
    onDeleteTag: s.onDeleteTag,
  }));

  const fetchSidebar = useSidebarStore((s) => s.fetchSidebar);

  const onUpdateFileNameAndIcon = useFileStore((s) => s.onUpdateFileNameAndIcon);

  useEffect(() => {
    currentFile?.name && setInputTxt(currentFile.name);
  }, [currentFile?.name]);

  const handleUpdateFields = async (values: { title?: string; icon?: any; color?: string }) => {
    setIsEmojiPickerOpen(false);

    if (currentFile?.id) {
      const response: any = await onUpdateFileNameAndIcon({
        fileId: currentFile?.id,
        icon: values.icon,
        name: values?.title,
      });

      if (response?.id) {
        fetchFile({ id: fileId });

        if (currentFile?.pin) fetchSidebar(router?.query?.mode as string);
      }
      if (values.color) await API.put(`/file?id=${fileId}`, values);
    }
  };

  const handleOnAddCover = async (coverImage: any) => {
    const response = await addCover({
      fileId,
      coverImage: typeof coverImage === 'string' ? coverImage : getRandomCoverImage(),
    });

    if (response?.data?.success) fetchFile({ id: fileId });
  };

  const handleOnDisableCover = async () => {
    await disableCover({ fileId });
    fetchFile({ id: fileId });
  };

  const toggleEmojiPicker = (value?: boolean) => () => {
    setIsEmojiPickerOpen(value ?? !isEmojiPickerOpen);
  };

  const handleChooseImg = useCallback(() => {
    hiddenFileInput.current.click();
  }, []);

  const handleFileUpload = async (file) => {
    const data = new FormData();
    data.append('file', file);
    const response = await API.post(endpoints.creationFile.url, data, {
      headers: { ['Content-Type']: 'multipart/form-data' },
    });

    return response.data?.data?.message?.url;
  };

  const onFileChange = useCallback(async (event) => {
    if (event.target.files.length) {
      const imgUrl = await handleFileUpload(event.target.files[0]);
      await handleUpdateFields({ icon: { id: imgUrl, type: 'image' } });
      await updateRecentIcon(imgUrl);
    }
  }, []);

  const handleRecentIcons = async () => {
    const images: recentImg[] = await getRecentIcons();
    await getRecentUploads();
    setRecentIconUploads(images);
  };

  const handleLink = () => {
    handleUpdateFields({ icon: { id: imgUrlRef.current.value, type: 'image' } });
  };
  const handleAddNewTag = () => {
    const tags = organisationTags || [];
    const isTagPresent = tags.some((item) => item.name === inputLabel);
    if (!isTagPresent) {
      const tagColor = fileTagColor[Math.floor(Math.random() * fileTagColor.length)];
      const tagId = uuid();
      tags.push({ name: inputLabel, id: tagId, color: tagColor });
      onAddTag({
        name: inputLabel,
        id: tagId,
        color: tagColor,
        fileId: fileId,
      });
    }
    setInputLabel('');
  };

  const handleLabelClick = (editTag: FileTag) => {
    setTagToBeEdited(editTag);
    setIsOpenEditTagModal(true);
  };
  const handleEditLabel = () => {
    if (tagtobeEdited) {
      const response: any = onUpdateTag({
        name: tagtobeEdited?.name,
        color: tagtobeEdited?.color,
        id: tagtobeEdited?.id,
        mode: 'update',
        fileId: fileId,
      });
      if (response) {
        setIsOpenEditTagModal(false);
        setTagToBeEdited({});
      }
    }
  };

  const handleAddTagToFile = (addTag: OrganisationTag) => {
    if (addTag?.isChecked) {
      const response: any = onUpdateTag({
        name: addTag?.name,
        color: addTag?.color,
        id: addTag?.id,
        mode: 'remove',
        fileId: fileId,
      });
    } else {
      const response: any = onUpdateTag({
        name: addTag?.name,
        color: addTag?.color,
        id: addTag?.id,
        mode: 'add',
        fileId: fileId,
      });
    }
    const tags = organisationTags || [];
    const index = tags.findIndex((tag) => tag.id === addTag.id);
    tags[index].isChecked = !addTag.isChecked;
    setOrganisationTags(tags);
  };

  const handleDeleteLabel = () => {
    if (tagtobeEdited?.id) {
      const response: any = onDeleteTag({
        id: tagtobeEdited?.id,
        fileId: fileId,
      });
      if (response) {
        setIsOpenDeleteTagModal(false);
        setTagToBeEdited({});
      }
    }
  };

  return (
    <div className="bg-white border-t border-gray-200 overflow-hidden">
      <div
        className={cn({
          'bg-white z-20 w-full overflow-hidden': true,
        })}
      >
        {currentFile?.showCover && currentFile?.coverImage && (
          <CoverImage
            coverImage={currentFile?.coverImage}
            onChangeCover={handleOnAddCover}
            onDisableCover={handleOnDisableCover}
            disabled={isReadOnly}
          />
        )}

        <div
          className={cn({
            'max-w-864 px-10 mx-auto ': router.query.type === 'document',
            'mobile:px-16 desktop:px-24 bigScreen:px-64': router.query.type !== 'document',
          })}
        >
          <div className={cn('mt-16 mb-10')}>
            <div className="flex flex-row">
              {!isReadOnly && !currentFile?.showCover && (
                <div className={cn('flex text-gray-500 pb-12')}>
                  <button className="flex mr-16" onClick={handleOnAddCover}>
                    <ImageIcon className="text-gray-500 mr-4 mt-4 block" />
                    Add cover
                  </button>
                </div>
              )}
              {/* <div className="text-gray-500 pb-18">
                <button className="flex mr-16 hover:text-gray-800" onClick={handleBoardSettings}>
                  <SettingsIcon className="mr-6 mt-4" />
                  Board settings
                </button>
              </div> */}
            </div>
            <div className="flex items-center">
              <Popover
                offset={[180, 10]}
                isOpen={isEmojiPickerOpen}
                onClose={toggleEmojiPicker(false)}
                onOpen={toggleEmojiPicker(true)}
                modifiers={[{ name: 'eventListeners', options: { scroll: isEmojiPickerOpen } }]}
              >
                <PopoverTrigger>
                  <button
                    disabled={isReadOnly}
                    className="border border-gray-100 shadow-md bg-gray-199 rounded-sm mr-12 flex items-center justify-center"
                    onClick={handleRecentIcons}
                  >
                    <div className="h-40 w-40">
                      {currentFile?.icon?.type === 'image' ? (
                        <img
                          className="w-25 h-25 object-center object-cover"
                          src={currentFile?.icon?.id}
                        />
                      ) : (
                        <div className="m-auto mt-4">
                          <Emoji
                            emoji={
                              (currentFile?.icon?.id as any) || {
                                id: 'bulb',
                              }
                            }
                            set="apple"
                            size={30}
                          />
                        </div>
                      )}
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <div
                    style={{ maxHeight: '70vh' }}
                    className="flex overflow-auto flex-col p-16 rounded-lg bg-white shadow-sm text-gray-999 w-368 relative"
                  >
                    <Tabs
                      selected={0}
                      size="small"
                      className="border-b font-regular text-xs items-center"
                    >
                      <Panel title="Gallery">
                        <div className="my-16">
                          <Picker
                            title="Pick your emoji…"
                            emoji="point_up"
                            sheetSize={32}
                            perLine={9}
                            showPreview={false}
                            showSkinTones={false}
                            onSelect={(e) => handleUpdateFields({ icon: { id: e.id } })}
                          />
                        </div>
                      </Panel>
                      <Panel title="Upload">
                        <div className="my-16">
                          <Button
                            status="primary"
                            className="mb-8 w-full justify-center px-15"
                            size="sm"
                            onClick={handleChooseImg}
                          >
                            Choose image
                          </Button>
                          <input
                            ref={hiddenFileInput}
                            accept="image/*"
                            className="hidden"
                            id="file"
                            multiple={false}
                            type="file"
                            onChange={onFileChange}
                          />
                          <div className="text-sm text-gray-600 mb-26">
                            Recommended size is 280 x 280 pixels
                          </div>
                          <div className="text-lg font-regular text-gray-600 my-10 mb-14">
                            Recent uploads
                          </div>
                          <div className="mt-16">
                            {recentIconUploads.map((image, index) => (
                              <button
                                className={cn('w-78 h-54', {
                                  'mr-8 mb-8': (index + 1) % 4 !== 0,
                                })}
                                onClick={() => {
                                  handleUpdateFields({
                                    icon: { id: image.src, type: 'image' },
                                  });
                                }}
                                key={`${image}-${index}`}
                              >
                                <img
                                  src={image.src}
                                  loading="lazy"
                                  className="w-full h-full object-center object-cover rounded-md"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      </Panel>
                      <Panel title="Link">
                        <div className="mt-16">
                          <div className="py-14 bg-gray-200 rounded-sm h-48 w-full px-12">
                            <input
                              placeholder="http://"
                              ref={imgUrlRef}
                              className="bg-gray-200 text-gray-500 font-regular placeholder-gray-500 w-full"
                            />
                          </div>
                          <Button
                            status="primary"
                            className="mt-16 float-right"
                            size="sm"
                            onClick={handleLink}
                          >
                            Embed image
                          </Button>
                        </div>
                      </Panel>
                    </Tabs>
                    <button
                      className="absolute rounded-md right-20 py-3 hover:bg-gray-100 font-regular text-sm items-center text-gray-500"
                      onClick={() => handleUpdateFields({ icon: { id: '' } })}
                    >
                      Remove
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <div className="flex w-full items-center justify-between">
                <input
                  placeholder="Untitled"
                  value={inputTxt}
                  className={cn('text-4xl font-semibold mr-12 bg-white w-full')}
                  onChange={(e) => setInputTxt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateFields({ title: inputTxt });
                    }
                  }}
                  onBlur={() => handleUpdateFields({ title: inputTxt })}
                  disabled={isReadOnly}
                />
                {type === 'agile' && (
                  <div className="flex justify-end mt-12 w-full">
                    <CreateEpic />
                    <CreateSprint />
                  </div>
                )}
              </div>
            </div>
            {!templateId && (
              <p className={cn('font-regular text-xs text-gray-500')}>
                Created by{' '}
                {typeof currentFile?.createdBy === 'string'
                  ? currentFile?.createdBy
                  : currentFile?.createdBy?.firstname}{' '}
                {currentFile?.sharedBy
                  ? `| Shared with you by ${
                      typeof currentFile?.sharedBy === 'string'
                        ? currentFile?.sharedBy
                        : currentFile?.sharedBy?.firstname
                    }`
                  : ''}
              </p>
            )}
          </div>

          <div className="flex mb-8">
            <div className="min-w-0 flex overflow-x-scroll no-scrollbar">
              {currentFile?.tags?.map((item) => {
                return (
                  <div
                    className="px-6 mr-8 text-white rounded-sm text-xs flex flex-shrink-0"
                    key={`${item.id}`}
                    style={{ lineHeight: '10px', backgroundColor: item.color }}
                    onMouseLeave={() => setTagHoverId('')}
                    onMouseEnter={() => setTagHoverId(item?.id || '')}
                  >
                    <button className="flex">
                      <span className="inline-block my-auto pr-2 py-2">{item.name}</span>
                      <span className="block">
                        {tagHoverId === item.id && !isReadOnly && (
                          <button className="mt-4" onClick={() => handleLabelClick(item)}>
                            <MoreIcon className="" />
                          </button>
                        )}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
            {!isReadOnly && (
              <Popover
                placement="bottom"
                isOpen={isOpenTagDropdown}
                onOpen={() => setIsOpenTagDropdown(true)}
                onClose={() => setIsOpenTagDropdown(false)}
                modifiers={[{ name: 'eventListeners', options: { scroll: isOpenTagDropdown } }]}
              >
                <PopoverTrigger>
                  <button className="text-blue-500 text-sm font-medium w-120 flex flex-shrink-0">
                    <AddIcon className="inline-block text-xs -mt-2 mr-2" /> Add tags
                  </button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <div
                      className="w-275 p-16 rounded-lg z-100 bg-white"
                      style={{ boxShadow: '0px 2px 16px rgba(19, 23, 32, 0.12)' }}
                    >
                      <input
                        className="bg-gray-100 p-10 text-sm w-full"
                        placeholder="Select a tag or create one"
                        onChange={(e) => setInputLabel(e.target.value)}
                        value={inputLabel}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddNewTag();
                          }
                        }}
                      />

                      <div className="max-h-384 overflow-y-scroll mt-16">
                        {currentFile?.organisationTags &&
                        currentFile?.organisationTags?.length > 0 ? (
                          organisationTags?.map((item) => {
                            return (
                              <div
                                style={{ lineHeight: '10px' }}
                                key={`${item.id}`}
                                className=" hover:bg-gray-100 w-full py-4 text-white rounded-sm px-6"
                              >
                                <button
                                  className="flex justify-between w-full "
                                  onClick={() => handleAddTagToFile(item)}
                                >
                                  <span
                                    className="inline-block px-6 rounded-sm text-xs py-4"
                                    style={{ backgroundColor: item.color }}
                                  >
                                    {item.name}
                                  </span>

                                  {item.isChecked && (
                                    <TickMarkIcon className="mt-4 text-blue-600" />
                                  )}
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-8 text-sm text-gray-600">No tags created yet</div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Portal>
              </Popover>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={isOpenDeleteTagModal} onClose={() => setIsOpenDeleteTagModal(false)}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '36rem',
            padding: '16px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <div className="flex justify-between mb-16">
            <p className="text-black font-medium text-lg">Delete tag?</p>
            <ModalCloseButton />
          </div>

          <p className="text-gray-600">
            This tag will be removed from all files in the organisation.
          </p>
          <div className="flex justify-end mt-12">
            <Button
              status="secondary"
              onClick={() => setIsOpenDeleteTagModal(false)}
              className=" text-gray-600 py-6 mr-12 font-medium text-sm"
            >
              Cancel
            </Button>
            {/* <button
              className="text-sm bg-blue-500 text-white border rounded-sm px-24 py-6"
             
            > 
              
            </button>*/}
            <Button status="danger" onClick={handleDeleteLabel}>
              Confirm
            </Button>
          </div>
        </ModalContent>
      </Modal>
      <Modal isOpen={isOpenEditTagModal} onClose={() => setIsOpenEditTagModal(false)}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '36rem',
            padding: '16px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <div className="flex justify-between mb-16">
            <p className="text-black font-medium text-lg">🏷 Edit tag</p>
            <button onClick={() => setIsOpenEditTagModal(false)}>
              <CloseIcon className="text-gray-600 text-xs" />
            </button>
          </div>
          <span className="text-gray-600 text-sm">Tag name</span>
          {tagtobeEdited && (
            <input
              className="bg-gray-100 p-10 text-sm w-full mt-8 rounded-sm border"
              onChange={(e) =>
                setTagToBeEdited((prevValue) => ({
                  ...prevValue,
                  ...{ name: e.target.value },
                }))
              }
              value={tagtobeEdited?.name}
            />
          )}
          <span className="text-gray-600 text-sm inline-block mt-16 mb-8">Color</span>
          <div className="flex mb-32">
            {fileTagColor.map((item) => {
              return (
                <button
                  key={item}
                  style={{
                    backgroundColor: item,
                    border: '1px solid #fff',
                    boxShadow: tagtobeEdited?.color === item ? `0 0 0 1px ${item}` : 'none',
                    width: '20px',
                    height: '20px',
                  }}
                  className={`rounded-full mr-8`}
                  onClick={() =>
                    setTagToBeEdited((prevValue) => ({
                      ...prevValue,
                      ...{ color: item },
                    }))
                  }
                />
              );
            })}
          </div>
          <hr />
          <div className="flex justify-between mt-12">
            <button
              className=" py-6 mr-12 font-medium text-sm text-red-500"
              onClick={() => {
                setIsOpenEditTagModal(false);
                setIsOpenDeleteTagModal(true);
              }}
            >
              <DeleteIcon className="mt-3" /> Delete tag
            </button>
            <div>
              <button
                onClick={() => setIsOpenEditTagModal(false)}
                className=" text-gray-600 py-6 mr-12 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                className="text-sm bg-blue-500 text-white border rounded-sm px-24 py-6"
                onClick={handleEditLabel}
              >
                Save
              </button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};
